import { lazy, type ComponentType } from 'react';

/**
 * Returns true if the given error looks like a "stale dynamic chunk"
 * failure thrown by Vite/Rollup-built ESM imports after a deploy. Exported
 * separately so the detection rule can be unit-tested without rendering
 * React.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message) ||
    /Loading chunk \d+ failed/i.test(error.message)
  );
}

/**
 * Wrap React.lazy() with a one-shot reload-on-stale-chunk recovery.
 *
 * When the deployed bundle ships new code-split chunk filenames (e.g. after
 * a deploy or hot HMR rebuild), an open browser tab that lazy-imports a
 * route still references the OLD filenames. The dynamic `import()` then
 * rejects with "Failed to fetch dynamically imported module" or
 * "Importing a module script failed", which previously bubbled up to our
 * `<ErrorBoundary>` and broke routes like `/patient-dashboard`.
 *
 * Strategy: on the FIRST failure (per-route, tracked in sessionStorage so we
 * don't loop forever), force a full page reload. The reload re-fetches the
 * latest `index.html`, which references the new chunk filenames, and the
 * lazy import succeeds on retry. If the failure happens again after a
 * reload, we re-throw and let the ErrorBoundary handle it — that means the
 * problem is real (network, server, syntax error in the chunk) and not just
 * stale cache.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  chunkName: string
) {
  return lazy(async () => {
    const reloadKey = `lazy-retry:${chunkName}`;
    try {
      const mod = await factory();
      // Clear any prior retry flag so a future stale-chunk event in the
      // same browser session can also auto-recover (otherwise the very
      // first reload "spends" the only retry for the lifetime of the tab).
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.removeItem(reloadKey);
        } catch {
          // sessionStorage can throw in private mode / quota; safe to ignore.
        }
      }
      return mod;
    } catch (error) {
      if (isStaleChunkError(error) && typeof window !== 'undefined') {
        const alreadyRetried = window.sessionStorage.getItem(reloadKey);
        if (!alreadyRetried) {
          window.sessionStorage.setItem(reloadKey, String(Date.now()));
          window.location.reload();
          // Return a never-resolving promise so React keeps showing the
          // Suspense fallback while the reload happens.
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw error;
    }
  });
}
